// src/HamburgerMenu.js
import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "./contexts/AuthContext";
import { useOrgInstallStore } from "./stores";
import TokenExpirationTimer from "./components/TokenExpirationTimer";
import UserProfileModal from "./UserProfileModal";
import DeviceClaimingModal from "./DeviceClaimingModal";
import UserManagementModal from "./UserManagementModal";
import logger from "./utils/logger";
import "./HamburgerMenu.css";

const HamburgerMenu = ({
  onNavigateToPatrons,
  onNavigateToDevices,
  onOpenAdvancedSettings,
  onOpenInstallationActions,
  onOpenInstallationManagement,
  onOpenOrganizationManagement,
  onToggleDevStreams,
  deviceCount = 0,
  devices = [],
  onDevicesClaimed,
  allDeviceStatesLoaded = false,
  currentOrgSettings = {},
  orgInstallContext: propOrgInstallContext,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isOrganizationsOpen, setIsOrganizationsOpen] = useState(false);
  const [isInstallationsOpen, setIsInstallationsOpen] = useState(false);
  const [isDevicesOpen, setIsDevicesOpen] = useState(false);
  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isDeviceClaimingModalOpen, setIsDeviceClaimingModalOpen] =
    useState(false);
  const [isUserManagementModalOpen, setIsUserManagementModalOpen] =
    useState(false);
  const { logout, user } = useAuth();

  // Org/Install Store - Individual selectors to avoid object recreation
  const organizationId = useOrgInstallStore(
    (state) => state.selectedOrganization?.orgId,
  );
  const organizationName = useOrgInstallStore(
    (state) => state.selectedOrganization?.name,
  );
  const installationId = useOrgInstallStore(
    (state) => state.selectedInstallation?.installationId,
  );
  const installationName = useOrgInstallStore(
    (state) => state.selectedInstallation?.name,
  );
  const organizations = useOrgInstallStore((state) => state.organizations);
  const selectedOrganization = useOrgInstallStore(
    (state) => state.selectedOrganization,
  );
  const navigateToOrgSelector = useOrgInstallStore(
    (state) => state.navigateToOrgSelector,
  );
  const navigateToInstallationSelector = useOrgInstallStore(
    (state) => state.navigateToInstallationSelector,
  );

  // Create fallback context from store
  const storeContext = useMemo(
    () => ({
      organizationId,
      organizationName,
      installationId,
      installationName,
      organizations,
      installations: selectedOrganization?.installations || [],
      changeOrganization: navigateToOrgSelector,
      changeInstallation: navigateToInstallationSelector,
    }),
    [
      organizationId,
      organizationName,
      installationId,
      installationName,
      organizations,
      selectedOrganization,
      navigateToOrgSelector,
      navigateToInstallationSelector,
    ],
  );

  // Use orgInstallContext from props if available (with proper handlers from OrgInstallWrapper),
  // otherwise use the store context
  const orgInstallContext = propOrgInstallContext || storeContext;

  // Log which context we're using
  useEffect(() => {
    logger.log("[HamburgerMenu] Context source", {
      receivedPropContext: !!propOrgInstallContext,
      usingPropContext: !!propOrgInstallContext,
      changeInstallationIsProp:
        propOrgInstallContext?.changeInstallation !==
        navigateToInstallationSelector,
      changeInstallationType: typeof orgInstallContext.changeInstallation,
    });
  }, [
    propOrgInstallContext,
    orgInstallContext.changeInstallation,
    navigateToInstallationSelector,
  ]);

  // Helper to check role (supports both string and object formats)
  const hasRole = (roleName) =>
    user?.roles?.some(
      (role) => (typeof role === "string" ? role : role?.name) === roleName,
    );

  // Check if user is Site Admin (not Full Admin or Org Admin)
  const isSiteAdmin =
    hasRole("Site Admin") &&
    !hasRole("Organization Admin") &&
    !hasRole("Full Admin");

  // Check if user can manage installations (Full Admin or Org Admin only)
  const canManageInstallations =
    hasRole("Full Admin") || hasRole("Organization Admin");

  // Check if user can manage organizations (Full Admin or Org Admin only)
  const canManageOrganizations =
    hasRole("Full Admin") || hasRole("Organization Admin");

  // Check if user can manage users (Full Admin or Org Admin only)
  const canManageUsers = hasRole("Full Admin") || hasRole("Organization Admin");

  // Check if user is Full Admin (for developer streams)
  const isFullAdmin = hasRole("Full Admin");

  // Enhanced access control for Developer Streams
  const canAccessDevStreams =
    isFullAdmin || currentOrgSettings?.enableDevStreamsForAllRoles;

  // Determine if Admin menu should be shown
  // Show Admin menu if user can manage users OR has dev streams access
  // Site Admins only see Admin menu if org has dev streams enabled
  const shouldShowAdminMenu = canManageUsers || canAccessDevStreams;

  // Debug logging for Admin menu visibility
  useEffect(() => {
    logger.log("[HamburgerMenu] Access Control Debug:", {
      userEmail: user?.email,
      userRoles: user?.roles,
      isSiteAdmin,
      isFullAdmin,
      canManageUsers,
      enableDevStreamsForAllRoles:
        currentOrgSettings?.enableDevStreamsForAllRoles,
      canAccessDevStreams,
      shouldShowAdminMenu,
    });
  }, [
    user,
    isSiteAdmin,
    isFullAdmin,
    canManageUsers,
    currentOrgSettings,
    canAccessDevStreams,
    shouldShowAdminMenu,
  ]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  // Auto-collapse Admin submenu when access changes
  useEffect(() => {
    if (!shouldShowAdminMenu && isAdminOpen) {
      setIsAdminOpen(false);
    }
  }, [shouldShowAdminMenu, isAdminOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest(".hamburger-menu-container")) {
        closeMenu();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Close menu on Escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && isOpen) {
        closeMenu();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  return (
    <>
      {/* Overlay */}
      {isOpen && <div className="hamburger-overlay" onClick={closeMenu}></div>}

      {/* Hamburger Menu Container */}
      <div className="hamburger-menu-container">
        {/* Hamburger Button */}
        <button
          className={`hamburger-button ${isOpen ? "open" : ""}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
          aria-expanded={isOpen}
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>

        {/* Slide-in Menu */}
        <nav className={`hamburger-menu ${isOpen ? "open" : ""}`}>
          <div className="menu-header">
            <h2>Menu</h2>
            <button
              className="close-button"
              onClick={closeMenu}
              aria-label="Close menu"
            >
              ×
            </button>
          </div>

          <div className="menu-body">
            <ul className="menu-links">
              {/* Organizations Menu */}
              <li className="submenu-container">
                <button
                  className="menu-button submenu-toggle"
                  onClick={() => setIsOrganizationsOpen(!isOrganizationsOpen)}
                >
                  <span className="link-icon">🏢</span>
                  <span className="link-text">Organizations</span>
                  <span
                    className={`submenu-arrow ${
                      isOrganizationsOpen ? "open" : ""
                    }`}
                  >
                    ▶
                  </span>
                </button>
                <ul className={`submenu ${isOrganizationsOpen ? "open" : ""}`}>
                  <li>
                    <button
                      className="menu-button"
                      onClick={() => {
                        closeMenu();
                        orgInstallContext.changeOrganization();
                      }}
                    >
                      <span className="link-icon">🔄🏢</span>
                      <span className="link-text">Change Organizations</span>
                    </button>
                  </li>
                  {canManageOrganizations && (
                    <li>
                      <button
                        className="menu-button"
                        onClick={() => {
                          closeMenu();
                          if (onOpenOrganizationManagement)
                            onOpenOrganizationManagement();
                        }}
                      >
                        <span className="link-icon">🛠️🏢</span>
                        <span className="link-text">Manage Organizations</span>
                      </button>
                    </li>
                  )}
                  {!isSiteAdmin && (
                    <li>
                      <button
                        className="menu-button"
                        onClick={() => {
                          closeMenu();
                          if (onOpenAdvancedSettings) onOpenAdvancedSettings();
                        }}
                      >
                        <span className="link-icon">⚙️🏢 </span>
                        <span className="link-text">
                          Advanced Organization Settings
                        </span>
                      </button>
                    </li>
                  )}
                </ul>
              </li>

              <li className="menu-divider"></li>

              {/* Sites Menu */}
              <li className="submenu-container">
                <button
                  className="menu-button submenu-toggle"
                  onClick={() => setIsInstallationsOpen(!isInstallationsOpen)}
                >
                  <span className="link-icon">🏠</span>
                  <span className="link-text">Sites</span>
                  <span
                    className={`submenu-arrow ${
                      isInstallationsOpen ? "open" : ""
                    }`}
                  >
                    ▶
                  </span>
                </button>
                <ul className={`submenu ${isInstallationsOpen ? "open" : ""}`}>
                  <li>
                    <button
                      className="menu-button"
                      onClick={() => {
                        closeMenu();
                        orgInstallContext.changeInstallation();
                      }}
                    >
                      <span className="link-icon">🔄🏠</span>
                      <span className="link-text">Change Sites</span>
                    </button>
                  </li>
                  {canManageInstallations && (
                    <li>
                      <button
                        className="menu-button"
                        onClick={() => {
                          closeMenu();
                          if (onOpenInstallationManagement)
                            onOpenInstallationManagement();
                        }}
                      >
                        <span className="link-icon">🛠️🏠</span>
                        <span className="link-text">Manage Sites</span>
                      </button>
                    </li>
                  )}
                  <li>
                    <button
                      className="menu-button"
                      onClick={() => {
                        closeMenu();
                        if (onOpenInstallationActions)
                          onOpenInstallationActions();
                      }}
                      disabled={deviceCount === 0}
                    >
                      <span className="link-icon">🌐🏠</span>
                      <span className="link-text">
                        Site-Wide Actions
                        {deviceCount > 0 && (
                          <span className="device-count-badge">
                            {deviceCount}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                </ul>
              </li>

              <li className="menu-divider"></li>

              {/* Devices Menu */}
              <li className="submenu-container">
                <button
                  className="menu-button submenu-toggle"
                  onClick={() => setIsDevicesOpen(!isDevicesOpen)}
                >
                  <span className="link-icon">🔐</span>
                  <span className="link-text">Devices</span>
                  <span
                    className={`submenu-arrow ${isDevicesOpen ? "open" : ""}`}
                  >
                    ▶
                  </span>
                </button>
                <ul className={`submenu ${isDevicesOpen ? "open" : ""}`}>
                  <li>
                    <button
                      className="menu-button"
                      onClick={() => {
                        closeMenu();
                        if (onNavigateToDevices) onNavigateToDevices();
                      }}
                    >
                      <span className="link-icon">🛠️🔐 </span>
                      <span className="link-text">Device Management</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className="menu-button"
                      onClick={() => {
                        closeMenu();
                        setIsDeviceClaimingModalOpen(true);
                      }}
                    >
                      <span className="link-icon">🫆|🥾🔐 </span>
                      <span className="link-text">Device Claiming</span>
                    </button>
                  </li>
                </ul>
              </li>

              {/* Patrons - top level, Org Admin and above only */}
              {canManageUsers && (
                <>
                  <li className="menu-divider"></li>
                  <li>
                    <button
                      className="menu-button"
                      onClick={() => {
                        closeMenu();
                        if (onNavigateToPatrons) onNavigateToPatrons();
                      }}
                    >
                      <span className="link-icon">👩🏽🎫</span>
                      <span className="link-text">Patrons</span>
                    </button>
                  </li>
                </>
              )}

              <li className="menu-divider"></li>

              {/* Admin Menu */}
              {shouldShowAdminMenu && (
                <li className="submenu-container">
                  <button
                    className="menu-button submenu-toggle"
                    onClick={() => setIsAdminOpen(!isAdminOpen)}
                  >
                    <span className="link-icon">🛡️🎛️</span>
                    <span className="link-text">Admin</span>
                    <span
                      className={`submenu-arrow ${isAdminOpen ? "open" : ""}`}
                    >
                      ▶
                    </span>
                  </button>
                  <ul className={`submenu ${isAdminOpen ? "open" : ""}`}>
                    {canManageUsers && (
                      <li>
                        <button
                          className="menu-button"
                          onClick={() => {
                            closeMenu();
                            setIsUserManagementModalOpen(true);
                          }}
                        >
                          <span className="link-icon">👥</span>
                          <span className="link-text">Manage Admins</span>
                        </button>
                      </li>
                    )}
                    {canAccessDevStreams && (
                      <li>
                        <button
                          className="menu-button"
                          onClick={() => {
                            closeMenu();
                            if (onToggleDevStreams) onToggleDevStreams();
                          }}
                        >
                          <span className="link-icon">📡</span>
                          <span className="link-text">
                            Developer: Event Streams
                          </span>
                        </button>
                      </li>
                    )}
                  </ul>
                </li>
              )}

              <li className="menu-divider"></li>

              {/* Mobile Location Services Menu */}
              <li className="submenu-container">
                <button
                  className="menu-button submenu-toggle"
                  onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                >
                  <span className="link-icon">📱⌚📍</span>
                  <span className="link-text">Mobile Location Services</span>
                  <span
                    className={`submenu-arrow ${
                      isMobileServicesOpen ? "open" : ""
                    }`}
                  >
                    ▶
                  </span>
                </button>
                <ul className={`submenu ${isMobileServicesOpen ? "open" : ""}`}>
                  <li>
                    <a
                      href="https://www.icloud.com/find/"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={closeMenu}
                    >
                      <span className="link-icon">🍎📱⌚</span>
                      <span className="link-text">Apple Find My Device</span>
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.google.com/android/find/?login"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={closeMenu}
                    >
                      <span className="link-icon">🤖📱⌚</span>
                      <span className="link-text">Android Find Hub</span>
                    </a>
                  </li>
                </ul>
              </li>
              <li>
                <a
                  href="https://developersupport.allegion.com/hc/en-us/requests"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMenu}
                >
                  <span className="link-icon">💬</span>
                  <span className="link-text">Give us Feedback!</span>
                </a>
              </li>
              <li>
                <a
                  href="https://yptraining.romancontrols.com/romanworks-userguide"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMenu}
                >
                  <span className="link-icon">📘</span>
                  <span className="link-text">Romanworks User Guide</span>
                </a>
              </li>
              <li>
                <a
                  href="/privacy-policy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMenu}
                >
                  <span className="link-icon">📜</span>
                  <span className="link-text">Privacy Policy</span>
                </a>
              </li>
            </ul>
          </div>

          <div className="menu-footer">
            {user?.email && (
              <div className="user-email">
                <span className="email-icon">👤</span>
                <div className="user-details">
                  <button
                    className="email-button"
                    onClick={() => setIsProfileModalOpen(true)}
                    aria-label="Edit profile"
                    title="Click to edit profile"
                  >
                    {user.email}
                  </button>
                  <div className="badges-row">
                    {user.roles && user.roles.length > 0 && (
                      <span className="user-role">{user.roles[0]}</span>
                    )}
                    <TokenExpirationTimer />
                  </div>
                </div>
              </div>
            )}
            <button
              className="logout-button"
              onClick={() => {
                closeMenu();
                logout();
              }}
            >
              <span className="link-icon">🚪</span>
              <span className="link-text">Logout</span>
            </button>
          </div>
        </nav>
      </div>

      {/* User Profile Modal */}
      <UserProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />

      {/* Device Claiming Modal */}
      <DeviceClaimingModal
        isOpen={isDeviceClaimingModalOpen}
        onClose={() => setIsDeviceClaimingModalOpen(false)}
        devices={devices}
        onDevicesClaimed={onDevicesClaimed}
      />

      {/* User Management Modal */}
      {isUserManagementModalOpen && (
        <UserManagementModal
          onClose={() => setIsUserManagementModalOpen(false)}
        />
      )}
    </>
  );
};

export default HamburgerMenu;
